'use client';

import { Spinner } from '@/components/ui/spinner';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import styles from '../../auth.module.css';
import { cn } from '@/lib/utils';
import { useSignIn } from '@clerk/nextjs';

const ClerkSignIn = dynamic(() => import('@clerk/nextjs').then((mod) => mod.SignIn), {
  ssr: false,
  loading: () => (
    <div className="flex flex-col items-center justify-center gap-3 py-16 w-full">
      <Spinner size="small" />
      <p className="text-sm text-gray-400">Loading sign in...</p>
    </div>
  )
});

interface SignInProps {
  forceRedirectUrl?: string;
  [key: string]: unknown;
}

const clerkAppearance = {
  elements: {
    cardBox: { boxShadow: 'none', border: 'none', borderRadius: '0', background: 'white' },
    card: { boxShadow: 'none', border: 'none', borderRadius: '0', background: 'white' },
    footer: { boxShadow: 'none', border: 'none', borderRadius: '0', background: 'white' },
    header: 'pb-4',
    headerTitle: 'text-2xl font-bold text-gray-900 tracking-tight',
    headerSubtitle: 'text-sm text-gray-400 font-normal',
    logoBox: 'mb-2',
    socialButtonsBlockButton:
      'border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-lg h-10 transition-colors',
    socialButtonsBlockButtonText: 'text-sm font-medium',
    dividerRow: 'my-4',
    dividerText: 'text-xs text-gray-400',
    dividerLine: 'bg-gray-200',
    formFieldLabel: 'text-sm font-medium text-gray-700',
    formFieldInput:
      'border border-gray-200 rounded-lg h-10 text-sm text-gray-900 focus:border-violet-500 focus:ring-violet-500 placeholder:text-gray-400',
    formButtonPrimary:
      'bg-gray-900 hover:bg-gray-800 text-white font-medium rounded-lg h-10 text-sm transition-colors',
    footerActionText: 'text-sm text-gray-500',
    footerActionLink: 'text-violet-600 font-medium hover:text-violet-700',
    identityPreviewText: 'text-sm text-gray-700',
    identityPreviewEditButton: 'text-violet-600 text-sm'
  }
};

const SignIn = (props: SignInProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { signIn } = useSignIn();

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`relative ${styles.authContainer}`}>
      <ClerkSignIn appearance={clerkAppearance} {...props} />
      {!signIn?.userData && (
        <div
          className={cn(
            'my-1 w-full bottom-0 text-xs text-gray-400 text-center pt-2',
            styles.authFooter,
            {
              [styles.loaded!]: isLoaded
            }
          )}>
          <div
            className={`flex flex-wrap items-center justify-center gap-1 ${styles.authFooterContent}`}>
            <span>By signing in, you agree to our</span>
            <a
              href="https://www.strique.io/privacy-policy"
              className="text-violet-600 font-medium hover:underline whitespace-nowrap"
              target="_blank"
              rel="noopener noreferrer">
              Privacy Policy
            </a>
            <span>and</span>
            <a
              href="https://www.strique.io/terms"
              className="text-violet-600 font-medium hover:underline whitespace-nowrap"
              target="_blank"
              rel="noopener noreferrer">
              Terms of Service
            </a>
            <span>.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export { SignIn };
